import axios from 'axios';


// Fetch Geocode Data using Google Maps API
export const fetchGeocode = async (searchTerm) => {
    const token = sessionStorage.getItem('accessToken');
    if (!token) {
        throw new Error('No access token found. User might not be authenticated.');
    }

    try {
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/get-geocode/`,
            {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    search_term: searchTerm
                }
            }
        );
        return response.data;
    } catch (err) {
        console.error("Failed to fetch location data", err);
        throw err;
    }
};


// Fetch Weather Data
export const fetchWeather = async (lat, lon) => {
    const token = sessionStorage.getItem('accessToken');
    if (!token) {
        throw new Error('No access token found. User might not be authenticated.');
    }

    try {
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/get-weather/`,
            {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    lat: lat,
                    long: lon,
                }
            }
        );
        return response.data;
    } catch (err) {
        console.error("Failed to fetch location data", err);
        throw err;
    }
};

// Fetch Location Data using Reverse Geocoding (Google Maps API)
export const fetchLocation = async (lat, lon) => {
    const token = sessionStorage.getItem('accessToken');
    if (!token) {
        throw new Error('No access token found. User might not be authenticated.');
    }

    try {
        const geoResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/get-google-map/`,
            {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    lat: lat,
                    long: lon,
                }
            }
        );
        return geoResponse;
    } catch (err) {
        console.error("Failed to fetch location data", err);
        throw err;
    }
};

// Export CSV Data
export const exportCSV = async (startDate, endDate, interval, siteId) => {
    const token = sessionStorage.getItem('accessToken');
    if (!token) {
        throw new Error('No access token found. User might not be authenticated.');
    }

    try {
        const exportResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/export-csv/`,
            {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    start_date: startDate,
                    end_date: endDate,
                    interval: interval,
                    site_id: siteId,
                },
                responseType: 'blob'
            }
        );

        const url = window.URL.createObjectURL(new Blob([exportResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `export_${startDate}_${endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
        console.error("Failed to export CSV data", err);
        throw err;
    }
};

// Export smart meter CSV Data
export const SMexportCSV = async (startDate, endDate, interval, siteId) => {
    const token = sessionStorage.getItem('accessToken');
    if (!token) {
        throw new Error('No access token found. User might not be authenticated.');
    }

    try {
        const exportResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/export-csv/`,
            {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    start_date: startDate,
                    end_date: endDate,
                    interval: interval,
                    site_id: siteId,
                },
                responseType: 'blob'
            }
        );

        const url = window.URL.createObjectURL(new Blob([exportResponse.data]));
        const link = document.createElement('a');
        link.href = url;

        let filename = `export_${startDate}_${endDate}.xlsx`;
        const disposition = exportResponse.headers['content-disposition'];
        if (disposition && disposition.includes("filename=")) {
            filename = disposition
                .split("filename=")[1]
                .replace(/"/g, "");
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
        console.error("Failed to export CSV data", err);
        throw err;
    }
};


// Export PDF Report Data
export const exportPDF = async (monthYear, afa, nem, siteId) => {
    const token = sessionStorage.getItem('accessToken');
    if (!token) {
        throw new Error('No access token found. User might not be authenticated.');
    }

    try {
        const exportResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/export-pdf/`,
            {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    date: monthYear,
                    afa,
                    nem,
                    site_id: siteId,
                },
                responseType: 'blob'
            }
        );

        const url = window.URL.createObjectURL(new Blob([exportResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = `export_${monthYear}_report.pdf`;
        const disposition = exportResponse.headers['content-disposition'];
        if (disposition && disposition.includes("filename=")) {
            filename = disposition
                .split("filename=")[1]
                .replace(/"/g, "");
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
        console.error("Failed to export PDF data", err);
        throw err;
    }
};


export const getClientAlarms = async (clientId) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/client-alarms/`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { site_id: clientId }
        });
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};


export const ecoslicense = async (master_key) => {
    try {
        const token = sessionStorage.getItem('accessToken');

        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/generate-license/`,
            {
                master_key: master_key
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error("API Error:", error.response ? error.response.data : error);
        return null;
    }
};


export const export_custom_pdf = async (client_id, formattedDate, include_bill = true, include_analysis = false, include_monthly = false, include_carbon_reporting = false, afa, nem,) => {
    const token = sessionStorage.getItem('accessToken');
    if (!token) {
        throw new Error('No access token found. User might not be authenticated.');
    }

    try {
        const exportResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/export-custom-pdf/`,
            {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    site_id: client_id,
                    date: formattedDate,
                    include_bill,
                    include_analysis,
                    include_monthly,
                    include_carbon_reporting,
                    afa,
                    nem,
                },
                responseType: 'blob',
            }
        );

        const contentType = exportResponse.headers['content-type'];
        if (!contentType || !contentType.includes('application/pdf')) {
            const text = await exportResponse.data.text();
            console.error('Unexpected response:', text);
            throw new Error(`Server returned non-PDF response: ${text}`);
        }

        const url = window.URL.createObjectURL(new Blob([exportResponse.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;

        let filename = `custom_export_${formattedDate}_report.pdf`;
        const disposition = exportResponse.headers['content-disposition'];
        if (disposition && disposition.includes("filename=")) {
            filename = disposition
                .split("filename=")[1]
                .replace(/"/g, "");
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);

    } catch (err) {
        if (err.response?.data instanceof Blob) {
            const text = await err.response.data.text();
            console.error('Server error response:', text);
            throw new Error(text);
        }
        console.error("Failed to export custom PDF data", err);
        throw err;
    }
};


// ── Smart Meters ──────────────────────────────────────────────────────────────

export const getSmartMeters = async (client_id) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/smart-meters/`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { client_id },
        });
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};


// ── Time Windows ──────────────────────────────────────────────────────────────

export const getTimeWindows = async (params) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/time-windows/`, {
            headers: { Authorization: `Bearer ${token}` },
            params,
        });
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

export const createTimeWindow = async (data, client_id) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        console.log("createTimeWindow payload:", { ...data, client_id }); // 👈
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/time-windows/`,
            { ...data, client_id },
            {
                headers: { Authorization: `Bearer ${token}` },
            }
        );
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

export const updateTimeWindow = async (id, data) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.patch(`${process.env.REACT_APP_API_URL}/api/time-windows/?id=${id}`, data, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

export const deleteTimeWindow = async (id) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/time-windows/?id=${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};


// ── Alarm Events ──────────────────────────────────────────────────────────────

export const getAlarmEvents = async (params) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/alarm-events/`, {
            headers: { Authorization: `Bearer ${token}` },
            params,
        });
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};


// ── Alarm Email Config ────────────────────────────────────────────────────────

export const getAlarmEmailConfig = async (client_id) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/alarm-email-config/`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { client_id },
        });
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

export const createAlarmEmailConfig = async (data) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/alarm-email-config/`, data, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

export const updateAlarmEmailConfig = async (id, data) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.patch(`${process.env.REACT_APP_API_URL}/api/alarm-email-config/?id=${id}`, data, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

export const get_sim_status = async () => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/get-sim-status/`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data.sims;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

export const get_sim_list = async () => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sims/list/`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data.sims;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

export const get_sim_detail = async (iccid) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sim-detail/`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { iccid },
        });
        return response.data;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

export const update_sim_label = async (iccid, label) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.put(
            `${process.env.REACT_APP_API_URL}/api/sim-label/${iccid}/`,
            { label },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};

export const get_sim_alerts = async () => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/sim-alerts/`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data.sim_alerts;
    } catch (error) {
        console.error("API Error:", error);
        return [];
    }
};

export const threshold_alarm_list = async (clientId) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/threshold-alarm-list/`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { site_id: clientId }
        });
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};


export const getDashboardAnalytics = async (selectedDate) => {
    try {
        const token = sessionStorage.getItem('accessToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/analytics/dashboard`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { date: selectedDate }
        });
        return response;
    } catch (error) {
        console.error("API Error:", error);
        return null;
    }
};